import React, { useState, useEffect } from 'react';

const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

const emptyForm = {
    title: '',
    description: '',
    location: '',
    country: '',
    propertyType: 'villa',
    pricePerNight: '',
    maxGuests: '',
    bedrooms: '',
    bathrooms: '',
    amenities: '',
    highlights: [''],
    rules: [''],
    tags: '',
    images: [],
    isActive: true,
    isFeatured: false,
};

const VillaModal = ({ show, onClose, villa, loadingVilla = false, onSave }) => {
    const [currentTab, setCurrentTab] = useState('basic');
    const [uploadingImages, setUploadingImages] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        location: '',
        country: '',
        propertyType: 'villa',
        pricePerNight: '',
        maxGuests: '',
        bedrooms: '',
        bathrooms: '',
        amenities: '',
        highlights: [''],
        rules: [''],
        tags: '',
        images: [],
        isActive: true,
        isFeatured: false,
    });

    useEffect(() => {
        if (!show) return;
        if (loadingVilla) return;

        if (villa) {
            setFormData({
                title: villa.title || '',
                description: villa.description || '',
                location: villa.location || '',
                country: villa.country || '',
                propertyType: villa.propertyType || 'villa',
                pricePerNight: villa.pricePerNight ?? '',
                maxGuests: villa.maxGuests ?? '',
                bedrooms: villa.bedrooms ?? '',
                bathrooms: villa.bathrooms ?? '',
                amenities: (villa.amenities || []).join(', '),
                highlights: villa.highlights?.length ? [...villa.highlights] : [''],
                rules: villa.rules?.length ? [...villa.rules] : [''],
                tags: (villa.tags || []).join(', '),
                images: villa.images || [],
                isActive: villa.isActive !== undefined ? villa.isActive : true,
                isFeatured: villa.isFeatured || false,
            });
        } else {
            setFormData(emptyForm);
        }
        setErrors({});
        setCurrentTab('basic');
    }, [villa, show, loadingVilla]);

    const validate = () => {
        const errs = {};
        if (!formData.title.trim()) errs.title = 'Title is required';
        if (!formData.description.trim()) errs.description = 'Description is required';
        if (!formData.location.trim()) errs.location = 'Location is required';
        if (!formData.country.trim()) errs.country = 'Country is required';
        if (formData.pricePerNight === '' || isNaN(parseFloat(formData.pricePerNight))) {
            errs.pricePerNight = 'Valid price per night is required';
        }
        if (formData.maxGuests === '' || isNaN(parseInt(formData.maxGuests, 10))) {
            errs.maxGuests = 'Max guests is required';
        }
        if (formData.bedrooms === '' || isNaN(parseInt(formData.bedrooms, 10))) {
            errs.bedrooms = 'Bedrooms is required';
        }
        if (formData.bathrooms === '' || isNaN(parseInt(formData.bathrooms, 10))) {
            errs.bathrooms = 'Bathrooms is required';
        }

        const basicKeys = ['title', 'description', 'location', 'country', 'pricePerNight', 'maxGuests', 'bedrooms', 'bathrooms'];
        const firstErrorTab = basicKeys.some(k => errs[k]) ? 'basic' : null;

        return { errs, firstErrorTab };
    };

    if (!show) return null;

    if (loadingVilla) {
        return (
            <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
                    <i className="fas fa-spinner fa-spin text-4xl text-brand-teal mb-4"></i>
                    <p className="text-gray-700 font-medium">Loading property details...</p>
                    <button type="button" onClick={onClose} className="mt-6 text-gray-500 hover:text-gray-700">Cancel</button>
                </div>
            </div>
        );
    }

    const inputCls = (field) =>
        `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none ${errors[field] ? 'border-red-500 bg-red-50' : ''}`;

    const tabs = [
        { id: 'basic', label: 'Basic Info', icon: 'fa-home' },
        { id: 'details', label: 'Details', icon: 'fa-list' },
        { id: 'media', label: 'Media', icon: 'fa-images' },
    ];

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setErrors(prev => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const updateListItem = (field, index, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].map((item, i) => i === index ? value : item)
        }));
    };

    const addListItem = (field) => setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));

    const removeListItem = (field, index) => setFormData(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
    }));

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploadingImages(true);
        try {
            const uploaded = await Promise.all(files.map(async (file) => {
                const fd = new FormData();
                fd.append('file', file);
                const res = await fetch(`${API_URL}/api/upload?subfolder=villas`, { method: 'POST', body: fd });
                if (!res.ok) throw new Error('Upload failed');
                const data = await res.json();
                return data.url;
            }));
            setFormData(prev => ({ ...prev, images: [...prev.images, ...uploaded] }));
        } catch (err) {
            alert('Image upload failed: ' + err.message);
        } finally {
            setUploadingImages(false);
            e.target.value = '';
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const { errs, firstErrorTab } = validate();
        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            if (firstErrorTab) setCurrentTab(firstErrorTab);
            alert(`Please fix the following before saving:\n• ${Object.values(errs).join('\n• ')}`);
            return;
        }

        const villaData = {
            title: formData.title.trim(),
            description: formData.description.trim(),
            location: formData.location.trim(),
            country: formData.country.trim(),
            propertyType: formData.propertyType,
            pricePerNight: parseFloat(formData.pricePerNight),
            maxGuests: parseInt(formData.maxGuests, 10),
            bedrooms: parseInt(formData.bedrooms, 10),
            bathrooms: parseInt(formData.bathrooms, 10),
            amenities: formData.amenities.split(',').map(a => a.trim()).filter(a => a),
            highlights: formData.highlights.filter(h => (h || '').trim()),
            rules: formData.rules.filter(r => (r || '').trim()),
            tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
            images: formData.images,
            isActive: formData.isActive,
            isFeatured: formData.isFeatured,
        };
        onSave(villaData);
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 overflow-hidden" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-brand-teal to-brand-cyan text-white p-6 flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold">{villa ? 'Edit Villa / Bungalow' : 'Add Villa / Bungalow'}</h2>
                        <p className="text-teal-100 text-sm mt-1">Fill in the property details</p>
                    </div>
                    <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl transform hover:rotate-90 transition">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b bg-gray-50">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setCurrentTab(tab.id)}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition ${currentTab === tab.id ? 'border-b-2 border-brand-teal text-brand-teal bg-white' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            <i className={`fas ${tab.icon} mr-2`}></i>{tab.label}
                        </button>
                    ))}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
                    {/* Basic Info Tab */}
                    {currentTab === 'basic' && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-gray-700 font-semibold mb-1">Title *</label>
                                    <input type="text" value={formData.title} onChange={e => updateField('title', e.target.value)} className={inputCls('title')} placeholder="e.g., Sunrise Villa Goa" />
                                    {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-gray-700 font-semibold mb-1">Description *</label>
                                    <textarea value={formData.description} onChange={e => updateField('description', e.target.value)} rows={3} className={inputCls('description')} />
                                    {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Location *</label>
                                    <input type="text" value={formData.location} onChange={e => updateField('location', e.target.value)} className={inputCls('location')} placeholder="e.g., Calangute, Goa" />
                                    {errors.location && <p className="text-red-600 text-sm mt-1">{errors.location}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Country *</label>
                                    <input type="text" value={formData.country} onChange={e => updateField('country', e.target.value)} className={inputCls('country')} placeholder="e.g., India" />
                                    {errors.country && <p className="text-red-600 text-sm mt-1">{errors.country}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Property Type *</label>
                                    <select value={formData.propertyType} onChange={e => updateField('propertyType', e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none">
                                        <option value="villa">Villa</option>
                                        <option value="bungalow">Bungalow</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Price Per Night (₹)</label>
                                    <input type="number" min="0" value={formData.pricePerNight} onChange={e => updateField('pricePerNight', e.target.value)} className={inputCls('pricePerNight')} />
                                    {errors.pricePerNight && <p className="text-red-600 text-sm mt-1">{errors.pricePerNight}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Max Guests</label>
                                    <input type="number" min="1" value={formData.maxGuests} onChange={e => updateField('maxGuests', e.target.value)} className={inputCls('maxGuests')} />
                                    {errors.maxGuests && <p className="text-red-600 text-sm mt-1">{errors.maxGuests}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Bedrooms</label>
                                    <input type="number" min="1" value={formData.bedrooms} onChange={e => updateField('bedrooms', e.target.value)} className={inputCls('bedrooms')} />
                                    {errors.bedrooms && <p className="text-red-600 text-sm mt-1">{errors.bedrooms}</p>}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Bathrooms</label>
                                    <input type="number" min="1" value={formData.bathrooms} onChange={e => updateField('bathrooms', e.target.value)} className={inputCls('bathrooms')} />
                                    {errors.bathrooms && <p className="text-red-600 text-sm mt-1">{errors.bathrooms}</p>}
                                </div>
                                <div className="flex items-center gap-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={formData.isActive} onChange={e => updateField('isActive', e.target.checked)} className="w-4 h-4 text-brand-teal" />
                                        <span className="text-gray-700 font-medium">Active</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={formData.isFeatured} onChange={e => updateField('isFeatured', e.target.checked)} className="w-4 h-4 text-brand-teal" />
                                        <span className="text-gray-700 font-medium">Featured</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Details Tab */}
                    {currentTab === 'details' && (
                        <div className="space-y-6">
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">Amenities</label>
                                <input type="text" value={formData.amenities} onChange={e => updateField('amenities', e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none" placeholder="e.g., WiFi, Pool, BBQ, Parking" />
                                <p className="text-xs text-gray-500 mt-1">Separate with commas</p>
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">Highlights</label>
                                {formData.highlights.map((h, idx) => (
                                    <div key={idx} className="flex gap-2 mb-2">
                                        <input type="text" value={h} onChange={e => updateListItem('highlights', idx, e.target.value)} className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none" placeholder={`Highlight ${idx + 1}`} />
                                        <button type="button" onClick={() => removeListItem('highlights', idx)} className="text-red-500 hover:text-red-700 px-2"><i className="fas fa-trash"></i></button>
                                    </div>
                                ))}
                                <button type="button" onClick={() => addListItem('highlights')} className="text-brand-teal hover:text-brand-cyan text-sm font-medium"><i className="fas fa-plus mr-1"></i>Add Highlight</button>
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">House Rules</label>
                                {formData.rules.map((r, idx) => (
                                    <div key={idx} className="flex gap-2 mb-2">
                                        <input type="text" value={r} onChange={e => updateListItem('rules', idx, e.target.value)} className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none" placeholder={`Rule ${idx + 1}`} />
                                        <button type="button" onClick={() => removeListItem('rules', idx)} className="text-red-500 hover:text-red-700 px-2"><i className="fas fa-trash"></i></button>
                                    </div>
                                ))}
                                <button type="button" onClick={() => addListItem('rules')} className="text-brand-teal hover:text-brand-cyan text-sm font-medium"><i className="fas fa-plus mr-1"></i>Add Rule</button>
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">Tags</label>
                                <input type="text" value={formData.tags} onChange={e => updateField('tags', e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none" placeholder="e.g., beachfront, luxury, family" />
                                <p className="text-xs text-gray-500 mt-1">Separate with commas</p>
                            </div>
                        </div>
                    )}

                    {/* Media Tab */}
                    {currentTab === 'media' && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">Property Images</label>
                                <label className={`flex items-center justify-center w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition ${uploadingImages ? 'border-gray-300 bg-gray-50 cursor-not-allowed' : 'border-brand-teal hover:bg-teal-50'}`}>
                                    <i className={`fas ${uploadingImages ? 'fa-spinner fa-spin' : 'fa-cloud-upload-alt'} mr-2 text-brand-teal`}></i>
                                    <span className="text-gray-600">{uploadingImages ? 'Uploading...' : 'Click to upload images'}</span>
                                    <input type="file" multiple accept="image/*" onChange={handleImageUpload} disabled={uploadingImages} className="hidden" />
                                </label>
                                <p className="text-xs text-gray-500 mt-1">Uploaded immediately to server (JPG, PNG, WEBP)</p>
                            </div>
                            {formData.images.length > 0 && (
                                <div className="grid grid-cols-3 gap-4">
                                    {formData.images.map((img, index) => (
                                        <div key={index} className="relative">
                                            <img src={img.startsWith('/') ? `${API_URL}${img}` : img} alt={`Villa ${index + 1}`} className="w-full h-32 object-cover rounded-lg" />
                                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }))} className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700">
                                                <i className="fas fa-times text-xs"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Footer buttons */}
                    <div className="flex gap-3 pt-6 mt-4 border-t">
                        <button type="button" onClick={onClose} className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:bg-gray-50 transition">Cancel</button>
                        <button type="submit" className="flex-1 bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition">
                            <i className="fas fa-save mr-2"></i>{villa ? 'Save Changes' : 'Add Villa'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default VillaModal;
